import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { escape, noop } from 'lodash';

import { ClaimContextProvider } from '~/components/ClaimContainer';
import SendEmailCommunicationCardContainer from '~/components/communications/EmailCommunicationCard/SendEmailCommunicationCardContainer';
import LoadingDialog from '~/components/LoadingDialog';
import { useFetchClaim } from '~/hooks/useFetchClaim';

import { getEmailText, getForwardedEmailParticipants } from '../utils';

const ForwardEmailCommunicationBaseDialog = ({
  communication,
  onClose,
  onSendEmail = noop,
  onForward,
  open,
  disableMinimized,
  onMinimized,
  shouldDisplaySenderEmail = true,
  isGeneralQueueEmail = false,
}) => {
  const allowForwardOriginalAttachmentsOnNonClaim = isGeneralQueueEmail;

  const [claim, isLoadingClaim, isErrorClaim, reloadClaim] = useFetchClaim(communication.claim_id);

  const ccContacts = communication.cc_contacts ? communication.cc_contacts : undefined;
  const toContacts = communication.to_contacts ? communication.to_contacts : undefined;

  const attachments = useMemo(() => {
    if (communication.claim_id) {
      return (communication.attachments || [])
        .filter((attachment) => !!attachment.document)
        .map((attachment) => ({ ...attachment.document, attachment_filename: attachment.file_name }));
    } else if (allowForwardOriginalAttachmentsOnNonClaim) {
      return communication.attachments || [];
    } else {
      return [];
    }
  }, [communication, allowForwardOriginalAttachmentsOnNonClaim]);

  const parentAttachments = !communication.claim_id ? attachments : undefined;

  const emailHtmlBody = communication.body_html
    ? communication.body_html
    : '<div>' + escape(communication.body_text).replace(/(\r\n|\n)/g, '<br/>') + '</div>';

  const { origEmailSender, origEmailTarget, targetRecipientType } = getForwardedEmailParticipants(communication);

  const emailText = getEmailText({
    origEmailSender,
    origEmailTarget,
    communication,
    ccContacts,
    emailHtmlBody,
    toContacts,
    targetRecipientType,
  });

  const shouldDisplayLoadingDialog = isErrorClaim || isLoadingClaim;

  const sendEmailCommunicationCardContainer = (
    <SendEmailCommunicationCardContainer
      onClose={onClose}
      onSendEmail={async () => {
        await onSendEmail();
        onClose();
      }}
      open={open}
      onMinimized={onMinimized}
      disableMinimized={disableMinimized}
      emailText={emailText}
      emailTitle={`Fwd: ${communication.subject}`}
      attachments={attachments}
      onSubmitOverride={
        onForward
          ? async (values) => {
              await onForward(values);
              onClose();
            }
          : null
      }
      isNotClaimRelated={!communication.claim_id}
      referredCommunicationId={communication.id}
      communication={communication}
      shouldDisplaySenderEmail={shouldDisplaySenderEmail}
      shouldOverrideTitleWithTemplate={false}
      shouldContactInitialBodyWithTemplate={true}
      parentAttachments={parentAttachments}
      shouldDisplaySupervisorSenderSelection={true}
    />
  );

  return (
    <>
      {shouldDisplayLoadingDialog ? (
        <LoadingDialog track="Forward Email" isError={isErrorClaim} />
      ) : communication.claim_id ? (
        <ClaimContextProvider claim={claim} refreshData={reloadClaim}>
          {sendEmailCommunicationCardContainer}
        </ClaimContextProvider>
      ) : (
        sendEmailCommunicationCardContainer
      )}
    </>
  );
};

ForwardEmailCommunicationBaseDialog.propTypes = {
  communication: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSendEmail: PropTypes.func,
  onMinimized: PropTypes.func,
  onForward: PropTypes.func,
  open: PropTypes.bool,
  disableMinimized: PropTypes.bool,
  shouldDisplaySenderEmail: PropTypes.bool,
  isGeneralQueueEmail: PropTypes.bool,
};

export default ForwardEmailCommunicationBaseDialog;
